import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../lib/firebase';
import { doc, getDoc, getDocFromServer, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { checkAndUpdateSubscriptionStatus } from '../../lib/auth';
import { logger } from '../../lib/logger';
import { shouldBypassSubscription } from '../../lib/platform';

interface SubscriptionContextType {
  isSubscribed: boolean;
  isLoading: boolean;
  daysLeftInTrial: number | null;
  checkAccess: () => Promise<boolean>;
  refreshSubscriptionStatus: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType>({
  isSubscribed: false,
  isLoading: true,
  daysLeftInTrial: null,
  checkAccess: async () => false,
  refreshSubscriptionStatus: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  logger.info('SubscriptionProvider initialized', { component: 'SubscriptionProvider' });

  const [user, loading] = useAuthState(auth);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [daysLeftInTrial, setDaysLeftInTrial] = useState<number | null>(null);

  // Check if current user is admin
  const isAdmin = user?.email === 'breadflowbybadabec@gmail.com';

  useEffect(() => {
    logger.info('SubscriptionProvider effect triggered', {
      component: 'SubscriptionProvider',
      authLoading: loading,
      hasUser: !!user,
      userId: user?.uid,
      isAdmin,
      authCurrentUser: !!auth.currentUser
    });

    // Wait for auth to finish loading
    if (loading) {
      logger.debug('Auth still loading, setting isLoading=true', {
        component: 'SubscriptionProvider'
      });
      setIsLoading(true);
      return;
    }

    if (!user) {
      logger.info('No user, resetting subscription state', {
        component: 'SubscriptionProvider'
      });
      setIsSubscribed(false);
      setIsLoading(false);
      setDaysLeftInTrial(null);
      return;
    }

    // Admin always has premium access
    if (isAdmin) {
      logger.info('Admin user detected, granting premium access', {
        component: 'SubscriptionProvider',
        userId: user.uid
      });
      setIsSubscribed(true);
      setIsLoading(false);
      setDaysLeftInTrial(null);
      return;
    }

    // WebViewGold iOS and Safari iOS/iPad users bypass subscription (Apple App Store compliance)
    if (shouldBypassSubscription()) {
      logger.info('iOS/iPad Safari or WebViewGold iOS detected - bypassing subscription checks (App Store compliance)', {
        component: 'SubscriptionProvider',
        userId: user.uid
      });
      setIsSubscribed(true);
      setIsLoading(false);
      setDaysLeftInTrial(null);
      return;
    }

    setIsLoading(true);

    // Initial fetch from server to bypass cache
    const fetchInitialData = async () => {
      let calculatedPremiumAccess = false;
      try {
        logger.group('Fetching initial subscription data');
        logger.info('Starting initial data fetch', {
          component: 'SubscriptionProvider',
          userId: user.uid,
          authCurrentUser: !!auth.currentUser,
          authCurrentUserId: auth.currentUser?.uid
        });

        // Double-check auth is stable before fetching
        if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
          logger.warn('Auth state mismatch, skipping subscription fetch', {
            component: 'SubscriptionProvider',
            userId: user.uid,
            authCurrentUserId: auth.currentUser?.uid
          });
          setIsLoading(false);
          logger.groupEnd();
          return;
        }

        logger.debug('Fetching user document from server', {
          component: 'SubscriptionProvider',
          userId: user.uid
        });
        const docSnapshot = await getDocFromServer(doc(db, 'users', user.uid));

        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          logger.info('Initial user subscription data from server', {
            component: 'SubscriptionProvider',
            userId: user.uid,
            subscriptionStatus: userData.subscriptionStatus,
            hasTrialEndDate: !!userData.trialEndDate,
            trialEndDate: userData.trialEndDate ? userData.trialEndDate.toDate().toISOString() : null,
            hasStripeCustomerId: !!userData.stripeCustomerId
          });

          // Check if user has premium access (paid or free with active trial)
          let hasPremiumAccess = false;

          if (userData.subscriptionStatus === 'paid') {
            hasPremiumAccess = true;
          } else if (userData.subscriptionStatus === 'free' && userData.trialEndDate) {
            const trialEnd = userData.trialEndDate.toDate();
            const now = new Date();
            if (trialEnd > now) {
              hasPremiumAccess = true;
            }
          }

          calculatedPremiumAccess = hasPremiumAccess;

          logger.info('Premium access calculated', {
            component: 'SubscriptionProvider',
            userId: user.uid,
            hasPremiumAccess
          });

          setIsSubscribed(hasPremiumAccess);

          // Calculate days left in trial if applicable
          if (userData.trialEndDate) {
            const trialEnd = userData.trialEndDate.toDate();
            const now = new Date();
            const diffTime = trialEnd.getTime() - now.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setDaysLeftInTrial(diffDays > 0 ? diffDays : 0);
            logger.debug('Trial days calculated', {
              component: 'SubscriptionProvider',
              userId: user.uid,
              daysLeft: diffDays > 0 ? diffDays : 0
            });
          } else {
            setDaysLeftInTrial(null);
          }
        } else {
          logger.warn('User document not found', {
            component: 'SubscriptionProvider',
            userId: user.uid
          });
          setIsSubscribed(false);
          setDaysLeftInTrial(null);
        }
        setIsLoading(false);
        logger.info('Initial data fetch completed', {
          component: 'SubscriptionProvider',
          userId: user.uid,
          isSubscribed: calculatedPremiumAccess
        });
        logger.groupEnd();
      } catch (error) {
        logger.error('Error fetching initial subscription status', error, {
          component: 'SubscriptionProvider',
          userId: user?.uid
        });
        setIsSubscribed(false);
        setIsLoading(false);
        logger.groupEnd();
      }
    };

    // Fetch initial data from server
    fetchInitialData();

    // Then subscribe to real-time updates
    const unsubscribe = onSnapshot(
      doc(db, 'users', user.uid),
      (docSnapshot) => {
        // Verify auth state is still valid
        if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
          console.warn('Auth state changed during subscription, ignoring update');
          return;
        }

        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          console.log('User subscription data updated:', {
            status: userData.subscriptionStatus,
            trialEndDate: userData.trialEndDate ? userData.trialEndDate.toDate() : null
          });

          // Check if user has premium access (paid or free with active trial)
          let hasPremiumAccess = false;

          if (userData.subscriptionStatus === 'paid') {
            hasPremiumAccess = true;
          } else if (userData.subscriptionStatus === 'free' && userData.trialEndDate) {
            const trialEnd = userData.trialEndDate.toDate();
            const now = new Date();
            if (trialEnd > now) {
              hasPremiumAccess = true;
            }
          }

          setIsSubscribed(hasPremiumAccess);

          // Calculate days left in trial if applicable
          if (userData.trialEndDate) {
            const trialEnd = userData.trialEndDate.toDate();
            const now = new Date();
            const diffTime = trialEnd.getTime() - now.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setDaysLeftInTrial(diffDays > 0 ? diffDays : 0);
          } else {
            setDaysLeftInTrial(null);
          }
        } else {
          setIsSubscribed(false);
          setDaysLeftInTrial(null);
        }
      },
      (error) => {
        console.error('Error fetching subscription status:', error);
        setIsSubscribed(false);
      }
    );

    return () => unsubscribe();
  }, [user, loading]);

  // Check if user has access to premium features
  const checkAccess = async (): Promise<boolean> => {
    if (!user) return false;

    // Admin always has access
    if (isAdmin) return true;

    // WebViewGold iOS and Safari iOS/iPad users always have access (App Store compliance)
    if (shouldBypassSubscription()) {
      console.log('iOS/iPad Safari or WebViewGold iOS - bypassing access check');
      return true;
    }

    console.log('Checking subscription access for user:', user.uid);

    try {
      // This will check if trial has expired and update status if needed
      const hasAccess = await checkAndUpdateSubscriptionStatus(user.uid);
      console.log('Subscription access check result:', hasAccess);
      setIsSubscribed(hasAccess);
      return hasAccess;
    } catch (error) {
      console.error('Error checking access:', error);
      return false;
    }
  };

  // Force refresh subscription status
  const refreshSubscriptionStatus = async (): Promise<void> => {
    if (!user) return;

    // Admin always has access, no need to refresh
    if (isAdmin) return;

    // WebViewGold iOS and Safari iOS/iPad users always have access, no need to refresh (App Store compliance)
    if (shouldBypassSubscription()) {
      console.log('iOS/iPad Safari or WebViewGold iOS - skipping subscription refresh');
      return;
    }

    // Guard against auth state changes during refresh
    if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
      console.warn('Auth state changed during refresh, aborting');
      return;
    }

    try {
      setIsLoading(true);
      const userRef = doc(db, 'users', user.uid);

      // Force check and update subscription status
      await checkAndUpdateSubscriptionStatus(user.uid);

      // Verify auth state is still valid after async operation
      if (!auth.currentUser || auth.currentUser.uid !== user.uid) {
        console.warn('Auth state changed during subscription update, aborting');
        setIsLoading(false);
        return;
      }

      // Use getDocFromServer to bypass cache
      const userSnap = await getDocFromServer(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();

        // Check if user has premium access (paid or free with active trial)
        let hasPremiumAccess = false;

        if (userData.subscriptionStatus === 'paid') {
          hasPremiumAccess = true;
        } else if (userData.subscriptionStatus === 'free' && userData.trialEndDate) {
          const trialEnd = userData.trialEndDate.toDate();
          const now = new Date();
          if (trialEnd > now) {
            hasPremiumAccess = true;
          }
        }

        setIsSubscribed(hasPremiumAccess);

        if (userData.trialEndDate) {
          const trialEnd = userData.trialEndDate.toDate();
          const now = new Date();
          const diffTime = trialEnd.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysLeftInTrial(diffDays > 0 ? diffDays : 0);
        }
      }
    } catch (error) {
      console.error('Error refreshing subscription status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SubscriptionContext.Provider
      value={{
        isSubscribed,
        isLoading,
        daysLeftInTrial,
        checkAccess,
        refreshSubscriptionStatus,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}